import { useEffect, useState } from 'react';
import { Button, message, Space, Spin, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AdminService } from '../Services/AdminService';
import { formatDateString } from '../Helpers/DateHelpers';
import { InvoiceStatus } from '../Types/InvoiceStatus';
import { WebApiAdminDtoCompanyInvoiceDto } from '../Repository/eventbookyapi';

const AdminCustomerInvoices = () => {
    const [invoices, setInvoices] = useState<WebApiAdminDtoCompanyInvoiceDto[]>([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        AdminService.GetCustomerInvoices()
            .then(setInvoices)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const setStatus = async (invoiceId: number, status: InvoiceStatus) => {
        try {
            await AdminService.SetInvoiceStatus(invoiceId, status);
            message.success(status === InvoiceStatus.Paid ? 'Invoice marked as paid' : 'Invoice marked as unpaid');
            load();
        } catch {
            message.error('Failed to update invoice status');
        }
    };

    const downloadInvoice = async (invoice: WebApiAdminDtoCompanyInvoiceDto) => {
        try {
            await AdminService.DownloadInvoicePdf(invoice.id!, invoice.invoiceNumber);
        } catch {
            message.error('Failed to download invoice');
        }
    };

    const columns: ColumnsType<WebApiAdminDtoCompanyInvoiceDto> = [
        {
            title: 'Company',
            dataIndex: 'companyName',
            key: 'companyName',
            sorter: (a, b) => (a.companyName ?? '').localeCompare(b.companyName ?? ''),
            render: (_, invoice) => <b>{invoice.companyName}</b>,
        },
        {
            title: 'Invoice number',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            sorter: (a, b) => (a.invoiceNumber ?? '').localeCompare(b.invoiceNumber ?? ''),
        },
        {
            title: 'Reference',
            dataIndex: 'customerReferenceName',
            key: 'customerReferenceName',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => Number(a.status ?? 0) - Number(b.status ?? 0),
            render: (_, invoice) => (
                Number(invoice.status) === InvoiceStatus.Paid
                    ? <Tag color="green">Paid</Tag>
                    : <Tag color="orange">New</Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime(),
            render: (_, invoice) => invoice.createdAt ? formatDateString(invoice.createdAt) : '–',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, invoice) => (
                <Space size="middle">
                    {invoice.blobUri && (
                        <Button onClick={() => downloadInvoice(invoice)}>Download</Button>
                    )}
                    {Number(invoice.status) === InvoiceStatus.Paid ? (
                        <Button onClick={() => setStatus(invoice.id!, InvoiceStatus.New)}>Mark as unpaid</Button>
                    ) : (
                        <Button type="primary" onClick={() => setStatus(invoice.id!, InvoiceStatus.Paid)}>Mark as paid</Button>
                    )}
                </Space>
            ),
        },
    ];

    if (loading) {
        return <Spin />;
    }

    return (
        <Table
            columns={columns}
            className="admin-table"
            style={{ marginTop: 0 }}
            rowKey="id"
            dataSource={invoices}
            scroll={{ x: 'max-content' }}
        />
    );
};

export default AdminCustomerInvoices;
